import { type FC, useEffect, useRef, useState } from 'react';

import type { CarePlanViewModel } from '@eluve/api-contract';
import {
  Button,
  Checkbox,
  Icon,
  Stack,
  Tag,
  Text,
  cn,
} from '@eluve/components';

const qualityTier = (done: number, total: number): string => {
  if (done >= total) return 'Complete';
  if (done >= total - 1) return 'Good';
  if (done >= Math.ceil(total / 2)) return 'Fair';
  return 'Weak';
};

export const ChecklistSection: FC<{
  checklist: CarePlanViewModel['checklist'];
  plan: CarePlanViewModel['plan'];
  reassessmentConfigured: boolean;
  disabled?: boolean;
  onToggleReassessBooked: (booked: boolean) => void;
  onConfirmCarePlan: () => void;
}> = ({
  checklist,
  plan,
  reassessmentConfigured,
  disabled,
  onToggleReassessBooked,
  onConfirmCarePlan,
}) => {
  const doneCount = checklist.items.filter((i) => i.done).length;
  const total = checklist.items.length;
  const midpoint = Math.ceil(checklist.items.length / 2);
  const left = checklist.items.slice(0, midpoint);
  const right = checklist.items.slice(midpoint);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => setStuck(entry ? !entry.isIntersecting : false),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const collapsed = stuck && !hovered;

  const renderItem = (
    item: CarePlanViewModel['checklist']['items'][number],
  ) => {
    const isUnavailableReassessment = item.manual && !reassessmentConfigured;

    return (
      <Stack key={item.key} direction="horizontal" align="center" gap={2}>
        {item.manual ? (
          <Checkbox
            checked={item.done}
            disabled={disabled || isUnavailableReassessment}
            onCheckedChange={(checked) =>
              onToggleReassessBooked(checked === true)
            }
            aria-label={item.label}
          />
        ) : item.done ? (
          <Icon name="Check" size="xs" className="text-text-positive" />
        ) : (
          <Icon name="X" size="xs" className="text-text-tertiary" />
        )}
        <Stack direction="vertical" gap={0}>
          <Text size="sm" color={item.done ? 'primary' : 'secondary'}>
            {item.label}
          </Text>
          {isUnavailableReassessment && (
            <Text size="xs" color="secondary">
              Set reassessment timing in Schedule first
            </Text>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <div ref={sentinelRef} aria-hidden className="h-px w-full" />
      <section
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          'sticky top-0 z-20 w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm transition-shadow',
          stuck && 'shadow-md',
        )}
      >
        <Stack direction="horizontal" align="center" justify="between" gap={3}>
          <Text weight="semibold">Care plan checklist</Text>
          <Tag
            size="s"
            color={doneCount >= total ? 'positive' : 'warning'}
            label={`${qualityTier(doneCount, total)} · ${doneCount} of ${total}`}
          />
        </Stack>
        <div
          className={cn(
            'overflow-hidden transition-all',
            collapsed ? 'mt-0 max-h-0 opacity-0' : 'mt-3 max-h-128 opacity-100',
          )}
        >
          <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
            <Stack direction="vertical" gap={2}>
              {left.map(renderItem)}
            </Stack>
            <Stack direction="vertical" gap={2}>
              {right.map(renderItem)}
            </Stack>
          </div>
          {plan && (
            <Stack
              direction="horizontal"
              align="center"
              wrap
              gap={3}
              className="mt-4 border-t border-border-secondary pt-3"
            >
              {plan.posture === 'accepted' ? (
                <>
                  <Icon
                    name="CheckCircle"
                    size="sm"
                    className="text-text-positive"
                  />
                  <Text size="sm" className="text-text-positive">
                    Plan active
                    {plan.startDate ? ` since ${plan.startDate}` : ''}
                  </Text>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    disabled={disabled || !checklist.completed}
                    onClick={onConfirmCarePlan}
                  >
                    <Icon name="Check" size="auto" />
                    Confirm Care Plan
                  </Button>
                  <Text size="sm" color="secondary">
                    Confirm to activate this plan and carry it over to future
                    visits.
                  </Text>
                </>
              )}
            </Stack>
          )}
        </div>
      </section>
    </>
  );
};
